import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './App.css';
import ProvideAppContext, { useAppContext } from './AppContext';
import Calendar from './Components/Calendar/Calendar';
import ErrorMessage from './ErrorMessage';
import ActivitiesPage from './Pages/ActivitiesPage';
import AnnouncementPage from './Pages/AnnouncementPage';
import DivisionsPage from './Pages/DivisionsPage';
import FaqsPage from './Pages/FaqsPage';
import Gallery from './Pages/gallery';
import SingleGallery from './Pages/gallery/SingleGallery';
import HomePage from './Pages/HomePage';
import InitiativesPage from './Pages/InitiativesPage';
import InnerPage from './Pages/InnerPage';
import MagazinesPage from './Pages/MagazinesPage';
import NewsPage from './Pages/news-page';
import NewsListPage from './Pages/news-page/NewsListPage';
import OffersPage from './Pages/OffersPage';
import PolicyPage from './Pages/PolicyPage';
import StructurePage from './Pages/StructurePage';
import VacanciesPage from './Pages/VacanciesPage';
import './style/css/media.css';
import './style/css/style.css';
import { useTranslation } from 'react-i18next';
import ProjectPage from './Pages/Projects/ProjectPage';
import ProjectNewsPage from './Pages/Projects/ProjectNewsPage';
import AttendancePage from './Pages/AttendancePage';
import CommunicationWithHCDivision from './Components/HR/CommunicationWithHCDivision';
import HrAnnouncements from './Components/HR/HrAnnouncements';
import TarabutSurvey from './Components/TarabutSurvey/TarabutSurvey';
import HRPolicy from './Components/HR/HRPolicy';
import GovernanceDocuments from './Components/Governance/GovernanceDocuments';

const queryClient = new QueryClient();

type AppProps = {
  pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
  const app = useAppContext();
  const { i18n } = useTranslation();

  // Memoize styles to set only on language change
  const updateStyles = useMemo(() => {
    return () => {
      document.documentElement.style.setProperty('--primary-color', '#19355C');
      document.documentElement.style.setProperty('--secondary-color', '#27995F');
      document.documentElement.style.setProperty('--highlight-color', '#868686');
      document.documentElement.style.setProperty('--accent-color', '#e74f3c');
      document.documentElement.style.setProperty('--extra-color', '#868686');
      document.documentElement.style.setProperty('--background-color', '#868686');
      document.documentElement.style.setProperty('--font-color', '#FFFFFF');
      document.documentElement.style.setProperty('--bs-primary', '#19355C');
      document.documentElement.style.setProperty('--bs-secondary', '#85A692');
      document.documentElement.style.setProperty('--bs-info', '#27995F');
      document.documentElement.style.setProperty('--bs-success', '#27995F');
      document.documentElement.style.setProperty('--bs-warning', '#e74f3c');
      document.documentElement.style.setProperty('--bs-danger', '#868686');
      document.documentElement.style.setProperty('--bs-light', '#868686');
      document.documentElement.style.setProperty('--bs-dark', '#FFFFFF');
      document.documentElement.style.setProperty('--bs-nav-link-color', '#19355C');
      document.documentElement.style.setProperty('--bs-link-color', '#19355C');
      
      const fontFamily = i18n.language === "ar" ? "'GE_SSTwoMedium', sans-serif" : "'Calibri', sans-serif";
      document.documentElement.style.setProperty('--font-family', fontFamily);
      document.documentElement.style.setProperty('--font-family_ar', "'GE_SSTwoMedium', sans-serif");
      document.documentElement.style.setProperty('--font-family_en', "'Calibri', sans-serif");
    };
  }, [i18n.language]);

  useEffect(() => {
    updateStyles();
  }, [updateStyles]);
  

  return (
    <MsalProvider instance={pca}>
      <ProvideAppContext>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              <Route path="/Announcement" element={<AnnouncementPage />} />
              <Route path="/Offers" element={<OffersPage />} />
              <Route path="/Structure" element={<StructurePage />} />
              <Route path="/Activities" element={<ActivitiesPage />} />
              <Route path="/Initiatives" element={<InitiativesPage />} />
              <Route path="/Magazines" element={<MagazinesPage />} />
              <Route path="/Vacancies" element={<VacanciesPage />} />
              <Route path="/Attendance" element={<AttendancePage />} />
              <Route path="/" element={<HomePage />} />
              <Route path="/pages/:id" element={<InnerPage />} />
              <Route path="/Divisions/:id" element={<DivisionsPage />} />
              <Route path="/projects/:id" element={<ProjectPage />} />
              <Route path="/projects/news/:id" element={<ProjectNewsPage />} />

              <Route path="/Policy/:id" element={<PolicyPage />} />
              <Route path="/gallery/:id" element={<SingleGallery />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/news" element={<NewsListPage />} />
              <Route path="/news-list/:id" element={<NewsPage />} />
              <Route path="/faq-list/:id/:category" element={<FaqsPage />} />
              <Route path="/Calendar" element={<Calendar />} />

              <Route path="/HR/CommunicationWithHCDivision" element={<CommunicationWithHCDivision />} />
              <Route path="/HR/Announcements" element={<HrAnnouncements />} />
              <Route path="/HR/Policy" element={<HRPolicy />} />
              
              {/* <Route path="/TarabutSurvey" element={<TarabutSurvey />} /> */}
              <Route path="/projects/:id/TarabutSurvey" element={<TarabutSurvey />} />

              
              <Route path="/GovernanceDocuments/:id" element={<GovernanceDocuments />} />
               

            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </ProvideAppContext>

      <ErrorMessage app={app} />
    </MsalProvider>
  );
}

export default App;
